<template>
  <main-template>
    <div class="novidades-page" style="padding-bottom: 6%">
      <div v-if="!isSuperAdmin" class="py-10">
        <v-alert type="error" outlined rounded="xl" class="pa-6 white">
          <div class="text-subtitle-1 font-weight-bold">Acesso Restrito</div>
          <div>
            O módulo de Novidades do Sistema é de acesso exclusivo para usuários com perfil
            Super-Admin.
          </div>
        </v-alert>
      </div>

      <div v-else>
        <v-card
          outlined
          rounded="xl"
          class="hero-banner pa-6 pa-md-8 mb-6 white--text elevation-3 overflow-hidden"
        >
          <div class="hero-decor-circle"></div>
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="8">
              <div class="d-flex align-center flex-wrap mb-2">
                <v-chip
                  color="amber darken-3"
                  dark
                  small
                  class="font-weight-black elevation-1 mr-3 mb-2"
                >
                  <v-icon left x-small>mdi-sparkles</v-icon>
                  Changelog Oficial
                </v-chip>
                <v-chip
                  color="rgba(255, 255, 255, 0.2)"
                  dark
                  small
                  class="font-weight-bold backdrop-blur mr-3 mb-2"
                >
                  <v-icon left x-small>mdi-tag-outline</v-icon>
                  Versão Atual: {{ latestVersionName }}
                </v-chip>
              </div>

              <h1 class="text-h4 font-weight-black white--text mb-2">Novidades do Sistema</h1>
              <p class="text-body-1 white--text text--lighten-4 mb-0 style-sub">
                Histórico automatizado de todas as versões, novos recursos, melhorias visuais e
                correções entregues na plataforma.
              </p>
            </v-col>
          </v-row>
        </v-card>

        <v-card
          outlined
          rounded="xl"
          class="pa-4 mb-8 white elevation-1 border-slate"
          v-if="releases.length > 0"
        >
          <div class="d-flex align-center flex-wrap">
            <span
              class="text-caption font-weight-black text-uppercase grey--text text--darken-2 mr-3 mb-2 d-flex align-center"
            >
              <v-icon small class="mr-1" color="var(--brand)">mdi-filter-variant</v-icon>
              Filtrar por Versão:
            </span>

            <v-chip
              :color="selectedVersion === null ? '#4f008f' : 'grey lighten-4'"
              :text-color="selectedVersion === null ? 'white' : 'grey darken-3'"
              class="font-weight-bold cursor-pointer filter-chip mr-3 mb-2"
              small
              @click="filterByVersion(null)"
            >
              Todas as Versões ({{ pagination.total || releases.length }})
            </v-chip>

            <v-chip
              v-for="rel in releases"
              :key="rel.id"
              :color="selectedVersion === rel.versao ? '#4f008f' : 'grey lighten-4'"
              :text-color="selectedVersion === rel.versao ? 'white' : 'grey darken-3'"
              class="font-weight-bold cursor-pointer filter-chip mr-3 mb-2"
              small
              @click="filterByVersion(rel.versao)"
            >
              {{ rel.versao }}
            </v-chip>
          </div>
        </v-card>

        <div v-if="loading" class="py-4">
          <v-card v-for="i in 3" :key="i" outlined rounded="xl" class="pa-6 mb-6 white">
            <v-skeleton-loader type="heading, paragraph, list-item-two-line"></v-skeleton-loader>
          </v-card>
        </div>

        <div v-else-if="filteredReleases.length === 0" class="py-10 text-center">
          <v-card outlined rounded="xl" class="pa-8 max-w-lg mx-auto white border-slate">
            <v-avatar color="var(--brand-soft)" size="64" class="mb-3">
              <v-icon color="var(--brand)" size="32">mdi-history</v-icon>
            </v-avatar>
            <div class="text-h6 font-weight-bold grey--text text--darken-3">
              Nenhuma release encontrada
            </div>
            <div class="text-body-2 grey--text text--darken-1 mt-1">
              {{
                selectedVersion
                  ? "Nenhum registro localizado para a versão selecionada."
                  : "Ainda não existem versões sincronizadas no sistema."
              }}
            </div>
            <v-btn
              v-if="selectedVersion"
              color="primary"
              text
              class="mt-3 font-weight-bold text-capitalize"
              @click="filterByVersion(null)"
            >
              Limpar Filtro
            </v-btn>
          </v-card>
        </div>

        <div v-else class="timeline-container px-2">
          <version-card
            v-for="(release, index) in filteredReleases"
            :key="release.id"
            :release="release"
            :is-latest="index === 0 && pagination.current_page === 1 && !selectedVersion"
          />

          <div v-if="pagination.last_page > 1" class="d-flex justify-center mt-8">
            <v-pagination
              v-model="pagination.current_page"
              :length="pagination.last_page"
              :total-visible="7"
              color="#4f008f"
              class="custom-pagination"
              @input="carregarNovidades"
            ></v-pagination>
          </div>
        </div>
      </div>
    </div>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";
import VersionCard from "@/components/Novidades/VersionCard.vue";

export default {
  name: "NovidadesPage",
  components: {
    VersionCard,
  },
  data() {
    return {
      loading: false,
      releases: [],
      selectedVersion: null,
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      },
    };
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    isSuperAdmin() {
      if (!this.user) return false;
      const roles = this.user.roles || [];
      const roleName = this.user.roleName || "";
      return roles.some((r) => r.name === "super-admin") || roleName === "super-admin";
    },
    filteredReleases() {
      if (!this.selectedVersion) return this.releases;
      return this.releases.filter((r) => r.versao === this.selectedVersion);
    },
    latestVersionName() {
      if (this.releases.length > 0) {
        return this.releases[0].versao;
      }
      return this.selectedVersion || "";
    },
  },
  async created() {
    await this.carregarNovidades();
  },
  methods: {
    async carregarNovidades(page = 1) {
      this.loading = true;
      try {
        const resp = await this.$services.novidadesService.getPublicReleases({
          page,
          per_page: this.pagination.per_page,
        });

        if (resp && resp.data) {
          this.releases = resp.data;
          if (resp.meta) {
            this.pagination.current_page = resp.meta.current_page;
            this.pagination.last_page = resp.meta.last_page;
            this.pagination.total = resp.meta.total;
          }
        }
      } catch (error) {
        /* eslint-disable-next-line no-console */
        console.error("Erro ao carregar novidades do sistema:", error);
        if (this.$toast) {
          this.$toast.error("Erro ao carregar novidades.");
        }
      } finally {
        this.loading = false;
      }
    },

    filterByVersion(versao) {
      this.selectedVersion = versao;
    },
  },
};
</script>

<style scoped>
.hero-banner {
  background: linear-gradient(135deg, #4f008f 0%, #29004d 100%);
  position: relative;
  border: none !important;
  box-shadow: 0 8px 32px rgba(79, 0, 143, 0.25) !important;
}

.hero-decor-circle {
  position: absolute;
  right: -50px;
  top: -50px;
  width: 250px;
  height: 250px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 70%);
  pointer-events: none;
}

.style-sub {
  opacity: 0.9;
  max-width: 650px;
}

.novidades-page {
  --novidades-borda: #cbd5e1;
}

.theme--dark .novidades-page {
  --novidades-borda: var(--border);
}

.border-slate {
  border-color: var(--novidades-borda) !important;
}

.filter-chip {
  transition: all 0.2s ease;
}

.filter-chip:hover {
  transform: translateY(-1px);
}

.cursor-pointer {
  cursor: pointer;
}
</style>
